import { Fragment, useState } from 'react';
import type { FC, ReactNode } from 'react';
import classnames from 'classnames';
import {
  useFloating,
  autoUpdate,
  useDismiss,
  useRole,
  useInteractions,
  shift,
  offset,
  useTransitionStatus,
  FloatingPortal,
  useClick,
  detectOverflow,
  type MiddlewareState,
  useFocus,
  flip
} from '@floating-ui/react';

import { getOffset, getPlacement } from 'app/utils/floating-ui';

import style from './style.module.scss';

interface Props {
  position?: Position;
  alignment?: Alignment;
  children: ReactNode;
  content: ReactNode;
  offset?: { x: number; y: number };
  className?: string;
  withPortal?: boolean;
  openedClassName?: string;
  tabIndex?: number;
  portalClassName?: string;
  enabled?: boolean;
}

const overlapReferenceMiddleware = {
  name: 'middleware',
  async fn(state: MiddlewareState) {
    const overflow = await detectOverflow(state);
    if (overflow.top > 0) {
      return { ...state, y: state.y + overflow.top };
    }
    if (overflow.bottom > 0) {
      return { ...state, y: state.y - overflow.bottom };
    }
    if (overflow.left > 0) {
      return { ...state, x: state.x + overflow.left };
    }
    if (overflow.right > 0) {
      return { ...state, x: state.x - overflow.right };
    }
    return state;
  }
};

const FloatingModal: FC<Props> = ({
  children,
  position = 'top',
  alignment = 'middle',
  offset: definedOffset,
  className,
  withPortal = false,
  content,
  openedClassName,
  tabIndex,
  portalClassName: portalClassNameOverride,
  enabled = true
}) => {
  const [open, setOpen] = useState(false);

  const { x, y, refs, strategy, context, placement } = useFloating({
    middleware: [
      offset(getOffset(definedOffset, position)),
      flip(),
      shift(),
      overlapReferenceMiddleware
    ],
    open,
    onOpenChange: setOpen,
    whileElementsMounted: autoUpdate,
    placement: getPlacement(position, alignment)
  });

  const click = useClick(context);
  const focus = useFocus(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'dialog' });

  const { getReferenceProps, getFloatingProps } = useInteractions([click, focus, dismiss, role]);

  const { isMounted, status } = useTransitionStatus(context, { duration: 300 });

  const modalClassName = classnames(style.modal, {
    [style.top]: placement.includes('top'),
    [style.left]: placement.includes('left'),
    [style.right]: placement.includes('right'),
    [style.bottom]: placement.includes('bottom')
  });

  const portalClassName = classnames(
    style.portal,
    {
      [style.top]: placement.includes('top'),
      [style.left]: placement.includes('left'),
      [style.right]: placement.includes('right'),
      [style.bottom]: placement.includes('bottom')
    },
    portalClassNameOverride
  );

  const Wrapper = withPortal ? FloatingPortal : Fragment;

  if (!enabled) {
    return <div className={classnames(style.container, className)}>{children}</div>;
  }

  return (
    <>
      <div
        ref={refs.setReference}
        className={classnames(style.container, className, { [openedClassName ?? '']: open })}
        {...getReferenceProps()}
        tabIndex={tabIndex}
      >
        {children}
      </div>
      {isMounted && (
        <Wrapper>
          <div
            ref={refs.setFloating}
            style={{
              position: strategy,
              top: y ?? 0,
              left: x ?? 0,
              width: 'max-content'
            }}
            {...getFloatingProps()}
            className={portalClassName}
            data-status={status}
          >
            <div className={modalClassName}>
              <div className={style.content}>{content}</div>
            </div>
          </div>
        </Wrapper>
      )}
    </>
  );
};

FloatingModal.displayName = 'FloatingModal';

export default FloatingModal;
